// Catalogue mirroring EngineeringBackbone.jsx — single source for sidebar nav,
// shared with both the embedded tab and the standalone /engineering route.
import { ListChecks, Anchor, GitMerge, Layers,
         PenTool, Cable, Map, ShieldAlert,
         RefreshCw, Package, Activity, Cloud,
         Sparkles, Eye, Lightbulb, BookOpen,
         PenLine, Boxes, Zap,
         Wand2, ShieldCheck, Calculator } from "lucide-react";

export const ENG_SECTIONS = [
  {
    key: "generative",
    title: "Generative Design",
    desc: "Wave Q — AI-generated design alternatives across all 3 domains (Stage A · A.2)",
    tabs: [
      { key: "generative-design", label: "Generative Design", icon: Wand2,
          desc: "Generate 5–50 design alternatives from constraints · score on cost / time / energy / safety / green / compliance · adopt the winner as project base design" },
    ],
  },
  {
    key: "library",
    title: "Library",
    desc: "Wave A — Engineering catalogues & registers",
    tabs: [
      { key: "specs",    label: "Pipe Class Library",  icon: ListChecks, desc: "Spec-driven pipe class catalogue · auto-select · material validator (ASME B31.3 / NACE)" },
      { key: "supports", label: "Pipe Support Manager",icon: Anchor,     desc: "Support catalogue · auto-place per ASME B31.3 Table 321.1 · 3-D manifest" },
      { key: "welds",    label: "Weld Management",     icon: GitMerge,   desc: "Auto-derive register · weld map · construction tracking dashboard" },
      { key: "drawings", label: "Drawing Register",    icon: Layers,     desc: "Drawing register · stage-gate workflow (DRAFT→IFR→IFA→APPROVED→IFC→AS-BUILT) · transmittals" },
    ],
  },
  {
    key: "generators",
    title: "Drawing Generators",
    desc: "Wave B — Auto-generate engineering deliverables",
    tabs: [
      { key: "iso",      label: "Isometric",                icon: PenTool,     desc: "Auto-generate piping isometric SVG (30°/30° projection · welds · supports · BOM) — Ch 4" },
      { key: "hookup",   label: "Instrument Hook-Up",       icon: Cable,       desc: "24 standard ISA hook-up templates (DP, radar, RTD, orifice, mag, control valve, PSV…) — Ch 12" },
      { key: "ga",       label: "GA / Orthographic",         icon: Map,         desc: "Project the 3-D model to PLAN / ELEVATION / SECTION views with discipline filter — Ch 13" },
      { key: "plotplan", label: "Plot Plan + Hazardous Area",icon: ShieldAlert, desc: "Top-down plot plan with API 505 / IEC 60079-10-1 hazardous zone classification — Ch 14" },
    ],
  },
  {
    key: "sync",
    title: "Sync & Analysis",
    desc: "Wave D — P&ID↔3D consistency, MTO, stress + PCF export, point clouds",
    tabs: [
      { key: "sync",       label: "P&ID ↔ 3D Sync",  icon: RefreshCw, desc: "Bidirectional consistency checker — equipment, lines, instruments, endpoint connections — Ch 2" },
      { key: "mto",        label: "MTO Extractor",   icon: Package,   desc: "Material take-off (piping by line + class · valves · steel · instruments) with XLSX/CSV export — Ch 7" },
      { key: "stress",     label: "Pipe Stress + PCF",icon: Activity, desc: "8-rule stress screener + Intergraph PCF export → Caesar II / AutoPIPE / CAEPIPE / ROHR2 — Ch 10" },
      { key: "pointcloud", label: "Point Cloud",     icon: Cloud,     desc: "Upload existing-plant laser scans (E57/LAS/LAZ/PTS/PLY/XYZ) for brownfield design — Ch 17" },
    ],
  },
  {
    key: "ai",
    title: "AI Layer",
    desc: "Wave E — Capexara Process Industry AI powered design intelligence",
    tabs: [
      { key: "command",      label: "Command Bar",      icon: Sparkles,  desc: "Natural-language design commands → structured edit operations on the PDO — Ch 19.1" },
      { key: "review",       label: "Design Reviewer",  icon: Eye,       desc: "AI-driven review across safety, operability, constructability, standards & cost — Ch 19.2" },
      { key: "autocomplete", label: "Auto-Complete",    icon: Lightbulb, desc: "Context-aware next-best-action suggestions when adding equipment / lines — Ch 19.3" },
      { key: "templates",    label: "Template Library", icon: BookOpen,  desc: "Pre-built reusable design assemblies (pump stations, PSV installs, flare systems, …) — Ch 19.4" },
    ],
  },
  {
    key: "editors",
    title: "Pro Editors",
    desc: "Wave C — Full-canvas P&ID, 3-D and SLD editors with AI assist",
    tabs: [
      { key: "pid-editor",   label: "P&ID Editor",       icon: PenLine, desc: "Drag-drop ISA-5.1 symbol palette · auto-tag · auto-line-id · 5-rule validation · revisions — Ch 5" },
      { key: "model-editor", label: "3-D Model Editor",  icon: Boxes,   desc: "Three.js viewport with parametric equipment · pipe routing · pipe-rack generator · clash detection — Ch 6" },
      { key: "sld-editor",   label: "SLD Editor",        icon: Zap,     desc: "Electrical SLD canvas · 27 symbols · feeder routing · cable ampacity + voltage-drop validation — Ch 11" },
    ],
  },
  {
    key: "documentation",
    title: "Documentation & Compliance",
    desc: "Wave T — Insurance-grade audit trail · peer review · certificates · regulatory packages",
    tabs: [
      { key: "documentation", label: "Documentation", icon: ShieldCheck,
          desc: "Tamper-evident audit trail · peer-review workflow · 5 compliance certificate templates · Regulatory / PE-stamp / PI-insurance bundles · sequential document numbering" },
    ],
  },
  {
    key: "cost-engineering",
    title: "Cost Engineering",
    desc: "Wave U — AI-powered cost estimation with market intelligence (Stage H)",
    tabs: [
      { key: "cost-estimation", label: "Cost Estimation", icon: Calculator,
          desc: "CPWD DSR 2024 base rates · 12-city location index · live material price database · industry benchmarks · what-if scenarios · AI optimisation suggestions · AACE estimate classes · PDF + XLSX reports" },
    ],
  },
];

export const FIRST_SECTION = ENG_SECTIONS[0].key;
export const FIRST_TAB     = ENG_SECTIONS[0].tabs[0].key;

export const tabKeys = ENG_SECTIONS.flatMap((s) => s.tabs.map((t) => t.key));

export function findSection(sectionKey) {
  return ENG_SECTIONS.find((s) => s.key === sectionKey);
}

export function findTab(tabKey) {
  for (const s of ENG_SECTIONS) {
    const t = s.tabs.find((x) => x.key === tabKey);
    if (t) return { section: s, tab: t };
  }
  return null;
}
